import React from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Copy, ChevronDown, Workflow } from "lucide-react";
import { copyToClipboard } from "@/lib/clipboard";

/**
 * Convert an outbound request object {method, url, headers, body, params}
 * to a copy-pasteable cURL command.
 */
function toCurl(req) {
  if (!req) return "";
  const method = (req.method || "POST").toUpperCase();
  const parts = [`curl -X ${method} '${req.url}'`];
  const headers = req.headers || {};
  Object.entries(headers).forEach(([k, v]) => {
    parts.push(`  -H '${k}: ${String(v).replace(/'/g, "'\\''")}'`);
  });
  if (req.body && method !== "GET") {
    const body = typeof req.body === "string" ? req.body : JSON.stringify(req.body);
    parts.push(`  -d '${body.replace(/'/g, "'\\''")}'`);
  }
  return parts.join(" \\\n");
}

/**
 * Convert an outbound request to an HTTPie command.
 * Example: `http POST https://api.example.com/foo X-Token:abc key1=val1 key2:='{"a":1}'`
 */
function toHttpie(req) {
  if (!req) return "";
  const method = (req.method || "POST").toUpperCase();
  const parts = ["http", method, `'${req.url}'`];
  const headers = req.headers || {};
  Object.entries(headers).forEach(([k, v]) => {
    parts.push(`'${k}:${String(v).replace(/'/g, "'\\''")}'`);
  });
  if (req.body && method !== "GET") {
    if (typeof req.body === "object" && !Array.isArray(req.body)) {
      // Emit each top-level key as an HTTPie field. Use `:=` for non-string values so
      // numbers / bools / nested objects stay JSON; `=` for strings.
      Object.entries(req.body).forEach(([k, v]) => {
        if (typeof v === "string") {
          parts.push(`${k}='${v.replace(/'/g, "'\\''")}'`);
        } else {
          parts.push(`${k}:='${JSON.stringify(v).replace(/'/g, "'\\''")}'`);
        }
      });
    } else {
      // Raw string body — pipe via echo
      const body = typeof req.body === "string" ? req.body : JSON.stringify(req.body);
      return `echo '${body.replace(/'/g, "'\\''")}' | ${parts.join(" ")}`;
    }
  }
  return parts.join(" ");
}

/**
 * Build a Postman v2.1 collection JSON containing this single request.
 * Importable directly into Postman / Insomnia.
 */
function toPostmanCollection(req) {
  if (!req) return "";
  const method = (req.method || "POST").toUpperCase();
  const url = new URL(req.url);
  const pathSegments = url.pathname.split("/").filter(Boolean);
  const queryItems = Array.from(url.searchParams.entries()).map(([k, v]) => ({ key: k, value: v }));

  const headersArr = Object.entries(req.headers || {}).map(([key, value]) => ({
    key,
    value: String(value),
    type: "text",
  }));

  let bodyBlock = null;
  if (req.body && method !== "GET") {
    const raw = typeof req.body === "string" ? req.body : JSON.stringify(req.body, null, 2);
    bodyBlock = {
      mode: "raw",
      raw,
      options: { raw: { language: "json" } },
    };
  }

  // Path segment name for the request — falls back to host/path
  const lastSeg = pathSegments[pathSegments.length - 1] || url.hostname;

  const collection = {
    info: {
      _postman_id: crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
      name: `FlexPay · ${lastSeg}`,
      description: "Exported from FlexPay API Tester",
      schema: "https://schema.getpostman.com/json/collection/v2.1.0/collection.json",
    },
    item: [
      {
        name: `${method} ${url.pathname}`,
        request: {
          method,
          header: headersArr,
          url: {
            raw: req.url,
            protocol: url.protocol.replace(":", ""),
            host: url.hostname.split("."),
            port: url.port || undefined,
            path: pathSegments,
            query: queryItems.length ? queryItems : undefined,
          },
          ...(bodyBlock ? { body: bodyBlock } : {}),
        },
        response: [],
      },
    ],
  };
  return JSON.stringify(collection, null, 2);
}

/**
 * Reusable copy dropdown for request panels.
 * Props:
 *   - request: outbound request object ({url, method, headers, body, params})
 *   - response: optional — response payload from the external API. When provided,
 *               an extra "External call (request + response)" option appears.
 *   - onSaveAsFlowStep: optional callback. When provided, a "Save as flow step"
 *                       option appears at the top of the menu.
 *   - size: button size (default "sm")
 *   - className: extra classes on trigger button
 */
export default function CopyRequestMenu({ request, response = undefined, onSaveAsFlowStep = null, size = "sm", className = "" }) {
  if (!request) return null;

  const copyText = (text, label) =>
    copyToClipboard(text, { successMessage: `Copied ${label}` });

  const copyJson = (obj, label) =>
    copyToClipboard(JSON.stringify(obj, null, 2), { successMessage: `Copied ${label}` });

  const hasHeaders = request.headers && Object.keys(request.headers).length > 0;
  const hasBody = request.body && (typeof request.body === "string" || Object.keys(request.body).length > 0);
  const hasParams = request.params && Object.keys(request.params).length > 0;
  const hasResponse = response !== undefined && response !== null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size={size}
          className={`h-6 text-[10px] ${className}`}
          data-testid="copy-request-menu"
        >
          <Copy className="h-3 w-3 mr-1" /> Copy <ChevronDown className="h-3 w-3 ml-0.5 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        {onSaveAsFlowStep && (
          <>
            <DropdownMenuItem onSelect={onSaveAsFlowStep} data-testid="save-as-flow-step">
              <Workflow className="h-3.5 w-3.5 mr-1.5 text-emerald-600" />
              <span className="font-semibold">Save as flow step</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
          Request parts
        </DropdownMenuLabel>
        <DropdownMenuItem onSelect={() => copyText(request.url || "", "URL")} data-testid="copy-url">
          <span className="font-medium">URL</span>
          <span className="ml-auto text-[10px] text-muted-foreground font-mono">.url</span>
        </DropdownMenuItem>
        {hasHeaders && (
          <DropdownMenuItem onSelect={() => copyJson(request.headers, "headers")} data-testid="copy-headers">
            <span>Headers</span>
            <span className="ml-auto text-[10px] text-muted-foreground font-mono">
              {Object.keys(request.headers).length}
            </span>
          </DropdownMenuItem>
        )}
        {hasParams && (
          <DropdownMenuItem onSelect={() => copyJson(request.params, "query params")} data-testid="copy-params">
            <span>Query params</span>
            <span className="ml-auto text-[10px] text-muted-foreground font-mono">
              {Object.keys(request.params).length}
            </span>
          </DropdownMenuItem>
        )}
        {hasBody && (
          <DropdownMenuItem onSelect={() => copyJson(request.body, "body")} data-testid="copy-body">
            <span>Body</span>
            <span className="ml-auto text-[10px] text-muted-foreground font-mono">JSON</span>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
          Whole
        </DropdownMenuLabel>
        <DropdownMenuItem onSelect={() => copyJson(request, "full request")} data-testid="copy-whole">
          <span className="font-semibold">Request only</span>
        </DropdownMenuItem>
        {hasResponse && (
          <DropdownMenuItem
            onSelect={() => copyJson({ request, response }, "external request + response")}
            data-testid="copy-external"
          >
            <span className="font-semibold">External call (req + resp)</span>
            <span className="ml-auto text-[10px] text-muted-foreground">📡</span>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={() => copyText(toCurl(request), "cURL")} data-testid="copy-curl">
          <span className="font-semibold">As cURL</span>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => copyText(toHttpie(request), "HTTPie command")} data-testid="copy-httpie">
          <span className="font-semibold">As HTTPie</span>
          <span className="ml-auto text-[10px] text-muted-foreground">terminal</span>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => copyText(toPostmanCollection(request), "Postman collection")} data-testid="copy-postman">
          <span className="font-semibold">As Postman collection</span>
          <span className="ml-auto text-[10px] text-muted-foreground">.json</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
